import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import { updateUser } from "./Actions/UserActions";
import Error404 from "./Common/Error/Error404";
import Account from "./Layout/Account/Account";
import { ApplicationEdit } from "./Layout/Applications/ApplicationEdit";
import { ApplicationHistory } from "./Layout/Applications/ApplicationHistory";
import Applications from "./Layout/Applications/Applications";
import Dialog from "./Layout/Dialog/Dialog";
import Documentation from "./Layout/Documentation/Documentation";
import { Header } from "./Layout/Header/Header";
import Home from "./Layout/Home/Home";
import Login from "./Layout/Login/Login";
import PartnerProgram from "./Layout/Partner/PartnerProgram";
import PartnerRegistered from "./Layout/Partner/PartnerRegistered";
import PartnerRegistration from "./Layout/Partner/PartnerRegistration";
import Register from "./Layout/Register/Register";
import ResetPassword from "./Layout/ResetPassword/ResetPassword";
import { type RootState } from "./Reducers/DeveloperReducers";
import PrivateRoute from "./Utilities/PrivateRoute";

const Developer: FC = () => {
    const [ fetchUser, setFetchUser ] = useState(true);
    const dispatch = useDispatch();
    const isAuthenticated = useSelector((state: RootState) => typeof state.user.user !== "boolean");

    useEffect(() => {
        setFetchUser(true);

        fetch("/user", {
            credentials: "same-origin",
            headers    : new Headers({
                "Accept": "application/json",
            }),
        })
            .then(response => {
                setFetchUser(false);

                if(response.status !== 200) {
                    throw new Error("Not logged in.");
                }

                return response.json();
            })
            .then(result => {
                dispatch(updateUser(result));
            })
            .catch(() => {
                dispatch(updateUser(false));
            });
    }, [ dispatch ]);

    return (
        <>
            <Header />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/documentation/*" element={<Documentation />} />
                <Route path="/partner" element={<PartnerProgram />} />
                <Route
                    path="/partner/register"
                    element={
                        <PrivateRoute
                            isAuthenticated={isAuthenticated}
                            isLoading={fetchUser}
                            component={PartnerRegistration}
                        />
                    }
                />
                <Route
                    path="/partner/registered"
                    element={
                        <PrivateRoute
                            component={PartnerRegistered}
                            isAuthenticated={isAuthenticated}
                            isLoading={fetchUser}
                        />
                    }
                />
                <Route
                    path="/application/:id/edit"
                    element={
                        <PrivateRoute
                            component={ApplicationEdit}
                            isLoading={fetchUser}
                            isAuthenticated={isAuthenticated}
                        />
                    }
                />
                <Route
                    path="/application/:id/history"
                    element={
                        <PrivateRoute
                            component={ApplicationHistory}
                            isLoading={fetchUser}
                            isAuthenticated={isAuthenticated}
                        />
                    }
                />
                <Route
                    path="/applications"
                    element={
                        <PrivateRoute
                            component={Applications}
                            isLoading={fetchUser}
                            isAuthenticated={isAuthenticated}
                        />
                    }
                />
                <Route
                    path="/account"
                    element={
                        <PrivateRoute
                            component={Account}
                            isLoading={fetchUser}
                            isAuthenticated={isAuthenticated}
                        />
                    }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/auth/reset" element={<ResetPassword />} />
                <Route element={<main><Error404 /></main>} />
            </Routes>
            <Dialog />
        </>
    );
};

export default Developer;
